/**
 * Client-side counterpart to server/src/merchant-qr.ts — parses a decoded EMVCo "Merchant
 * Presented QR" (Thai QR Payment, tag 30) payload to detect which of K-Shop / Mae Manee /
 * Tungngern it belongs to and pull out the fields the payment-account form needs, so an admin
 * can upload a QR photo from their own app instead of typing the shop code by hand.
 *
 * The three `BANK_REF` constants below must stay byte-identical to server/src/merchant-qr.ts's
 * `PROVIDERS[...].bankRef` — there is no shared package between the client/server workspaces,
 * so keep both files in sync by hand. Do not validate the CRC here: a QR the organizer just
 * photographed from their own banking app is already trusted (jsQR only reports a hit once the
 * QR's own Reed-Solomon error correction confirms the scanned bytes are intact).
 */

export type ThaiMerchantQrProvider = 'kshop' | 'mae_manee' | 'tungngern';

export interface ParsedMerchantQr {
  provider: ThaiMerchantQrProvider;
  ref1: string;
  /** Tungngern only — its tag30 sub-03 transaction code, not derivable from ref1. */
  txnCode?: string;
}

export type ParseThaiQrResult =
  | { ok: true; result: ParsedMerchantQr }
  | { ok: false; reason: 'not_tlv' | 'no_tag30' | 'unknown_provider' | 'malformed' };

const MERCHANT_AID = 'A000000677010112';

// keep in sync with server/src/merchant-qr.ts's PROVIDERS[...].bankRef
const BANK_REF: Record<ThaiMerchantQrProvider, string> = {
  kshop: '010753600031508',
  mae_manee: '010753600010286',
  tungngern: '010753700088205',
};

interface TlvField {
  tag: string;
  value: string;
}

/** Tolerant TLV walker: stops (rather than throwing) on any malformed id/len it can't parse. */
function parseTlv(payload: string): TlvField[] | null {
  const fields: TlvField[] = [];
  let i = 0;
  while (i < payload.length) {
    if (i + 4 > payload.length) return null;
    const tag = payload.slice(i, i + 2);
    const lenStr = payload.slice(i + 2, i + 4);
    const len = Number(lenStr);
    if (!/^\d{2}$/.test(tag) || !/^\d{2}$/.test(lenStr) || Number.isNaN(len)) return null;
    const value = payload.slice(i + 4, i + 4 + len);
    if (value.length !== len) return null;
    fields.push({ tag, value });
    i += 4 + len;
  }
  return fields;
}

export function parseThaiMerchantQr(payload: string): ParseThaiQrResult {
  const top = parseTlv(payload.trim());
  if (!top) return { ok: false, reason: 'not_tlv' };

  const tag30 = top.find((f) => f.tag === '30');
  if (!tag30) return { ok: false, reason: 'no_tag30' };

  const sub = parseTlv(tag30.value);
  if (!sub) return { ok: false, reason: 'malformed' };

  const aid = sub.find((f) => f.tag === '00')?.value;
  const bankRef = sub.find((f) => f.tag === '01')?.value;
  const ref1 = sub.find((f) => f.tag === '02')?.value;
  if (aid !== MERCHANT_AID || !bankRef || !ref1) return { ok: false, reason: 'malformed' };

  const provider = (Object.keys(BANK_REF) as ThaiMerchantQrProvider[]).find((p) => BANK_REF[p] === bankRef);
  if (!provider) return { ok: false, reason: 'unknown_provider' };

  if (provider === 'tungngern') {
    const txnCode = sub.find((f) => f.tag === '03')?.value;
    if (!txnCode) return { ok: false, reason: 'malformed' };
    return { ok: true, result: { provider, ref1, txnCode } };
  }

  return { ok: true, result: { provider, ref1 } };
}
